import { useBrokers } from '@/hooks/useBrokers';
import { useSessions } from '@/hooks/useSessions';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Key, Eye, EyeOff, Trash2, CheckCircle2, Circle, AlertTriangle, Copy, Check } from 'lucide-react';
import { BrokerName } from '@/types/broker';
import { brokerCredentialsService, BrokerCredential } from '@/services/brokerCredentials';
import { useToast } from '@/hooks/use-toast';
import { brokerKeySchema } from '@/lib/validationSchemas';
import { PhemexReferralInline } from '@/components/PhemexReferralBanner';
import { BuilderFeeApproval } from '@/components/BuilderFeeApproval';
import { MetaTraderPairing, MetaTraderKeyStatus } from '@/components/MetaTraderPairing';
import { intlLocale, dateLocale } from '@/lib/format';
import { track, keyErrorClass } from '@/lib/analytics';
import { TRADING_IPS, tradingIpsForClipboard } from '@/lib/tradingIps.core';

const BROKER_OPTIONS: BrokerName[] = ['Phemex'];
/*
 * KuCoin and Kraken, admin only until each passes its launch gate (Guillaume,
 * 2026-09-28). The server enforces it too (BROKER_PREVIEW_USERS); hiding them
 * here is only so nobody else is offered something they cannot save.
 */
const PREVIEW_BROKERS: BrokerName[] = ['KuCoin', 'Kraken', 'Hyperliquid', 'MetaTrader'];

/*
 * Each broker's own names for the parts of its key, so the form reads like the
 * page the user just copied them from. KuCoin's key has a third part, the
 * passphrase the user chose when creating it (Guillaume, 2026-09-28: "if
 * KuCoin is selected, we should be able to enter all 3").
 */
type KeyFields = {
  key: string; keyPlaceholder: string;
  secret: string; secretPlaceholder: string;
  passphrase?: string;
  passphrasePlaceholder?: string;
  // A line under the broker picker: what to do on the broker's own site first.
  note?: string;
};
const KEY_FIELDS: Record<BrokerName, KeyFields> = {
  Phemex: {
    key: 'broker.form.passKey', keyPlaceholder: 'broker.form.passKeyPlaceholder',
    secret: 'broker.form.secretKey', secretPlaceholder: 'broker.form.secretKeyPlaceholder',
  },
  KuCoin: {
    key: 'broker.form.apiKey', keyPlaceholder: 'broker.form.apiKeyPlaceholder',
    secret: 'broker.form.apiSecret', secretPlaceholder: 'broker.form.apiSecretPlaceholder',
    passphrase: 'broker.form.apiPassphrase',
  },
  Kraken: {
    key: 'broker.form.publicKey', keyPlaceholder: 'broker.form.publicKeyPlaceholder',
    secret: 'broker.form.privateKey', secretPlaceholder: 'broker.form.privateKeyPlaceholder',
  },
  /*
   * Hyperliquid is signed, not keyed (2026-11-02): the user approves an API
   * wallet on their own account and pastes ITS address and private key, plus
   * their main wallet's address, which is where the money is and what every
   * read is about. The main wallet's own private key is never asked for.
   */
  Hyperliquid: {
    key: 'broker.form.hlAgentAddress', keyPlaceholder: 'broker.form.hlAgentAddressPlaceholder',
    secret: 'broker.form.hlAgentKey', secretPlaceholder: 'broker.form.hlAgentKeyPlaceholder',
    passphrase: 'broker.form.hlMainWallet', passphrasePlaceholder: 'broker.form.hlMainWalletPlaceholder',
    note: 'broker.form.hlNote',
  },
  /*
   * MetaTrader is paired, not keyed (2026-11-03): the form below is replaced by
   * the pairing panel (MetaTraderPairing), so these fields are never shown.
   */
  MetaTrader: {
    key: 'broker.form.apiKey', keyPlaceholder: 'broker.form.apiKeyPlaceholder',
    secret: 'broker.form.apiSecret', secretPlaceholder: 'broker.form.apiSecretPlaceholder',
  },
  Bitget: {
    key: 'broker.form.apiKey', keyPlaceholder: 'broker.form.apiKeyPlaceholder',
    secret: 'broker.form.apiSecret', secretPlaceholder: 'broker.form.apiSecretPlaceholder',
    passphrase: 'broker.form.passphrase',
  },
};

interface BrokerApiKeyDialogProps {
  children: React.ReactNode;
}

/*
 * The addresses to allowlist on the broker key, and ONE button that copies all
 * of them at once, comma-separated, ready to paste into Phemex's "Bind IP"
 * field (Guillaume, 2026-10-10). The list is lib/tradingIps.core.ts, shared
 * byte for byte with the phone app.
 */
export function TradingIpsCopy() {
  const { t } = useTranslation('copy');
  const [copied, setCopied] = useState(false);
  // Clipboard refused: the addresses appear, selectable, so nothing is lost.
  const [refused, setRefused] = useState(false);
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(tradingIpsForClipboard());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setRefused(true);
    }
  };
  return (
    // One "Copy" button, not the addresses themselves (round 8 #226, Guillaume 10-27).
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button type="button" size="sm" variant="outline" onClick={copyAll} aria-label={t('broker.security.ipCopyAll', { count: TRADING_IPS.length })}>
        {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
        {copied ? t('action.copied') : t('action.copy')}
      </Button>
      {refused && (
        <code className="font-mono text-foreground bg-muted px-2 py-1 rounded select-all break-all">
          {tradingIpsForClipboard()}
        </code>
      )}
    </div>
  );
}

export function BrokerApiKeyDialog({ children }: BrokerApiKeyDialogProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('copy');
  const brokers = useBrokers();
  const brokerOptions = [...BROKER_OPTIONS, ...PREVIEW_BROKERS].filter((b) => brokers.visible(b));
  const { t: tm } = useTranslation('mobile');
  // Which keys a running session trades on, and how many sessions (round 8 #227).
  const { data: mySessions, isLoading: sessionsLoading } = useSessions();
  const keysInUse = new Map<number, number>();
  // Nothing is said about a key until the sessions are read: a pending list is not "not in use".
  for (const x of (sessionsLoading || !mySessions ? [] : mySessions) as { credential_id?: number; status?: string }[]) {
    const id = Number(x.credential_id);
    if (id > 0 && (x.status === 'active' || x.status === 'running')) keysInUse.set(id, (keysInUse.get(id) ?? 0) + 1);
  }
  const [credentials, setCredentials] = useState<BrokerCredential[]>([]);
  // Whether the list has come back once: "no keys yet" only after it has.
  const [credentialsLoaded, setCredentialsLoaded] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<BrokerName>('Phemex');
  const [connectionName, setConnectionName] = useState('');
  const [passKey, setPassKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  /*
   * Every key added here is a live key now.
   *
   * This defaulted to `true`, so a key was saved as testnet unless the user
   * noticed a switch below the fold -- which is how 8 of the 14 keys on the
   * platform came to be testnet, most of them by not noticing rather than on
   * purpose. Testnet is retired as a user route (Guillaume, 2026-09-04):
   * its prices drift from the real market and it does not list every asset, and
   * paper trading now does the practice job properly, at live prices with real
   * fees. The constant stays rather than the value being inlined, so the shape
   * of the request is unchanged and the day this comes back is a one-line day.
   */
  const isTestnet = false;

  /*
   * Per-key verification, which the phone has had and the web has not.
   *
   * `brokerCredentialsService.verifyCredential` has existed in this codebase
   * the whole time with nothing calling it -- the method was written, the
   * button never was. Meanwhile a user has been running a session since 4
   * August behind a key Phemex rejects with 401 on every read, and nothing on
   * this screen said so: the "Active" badge below is OUR row flag, not the
   * exchange's answer.
   *
   * Keyed by credential id rather than a single "last result", so verifying one
   * key does not clear the verdict on another.
   */
  const [verifying, setVerifying] = useState<string | null>(null);
  const [verdicts, setVerdicts] = useState<Record<string, { ok: boolean; error?: string; venue?: string; available?: number; total?: number }>>({});
  /*
   * Which key this browser is actually trading on. NOT credential.is_active,
   * which the server sets true at creation and never changes — see
   * brokerCredentials.getActiveCredentialId.
   */
  const [activeCredentialId, setActiveCredentialId] = useState<string | null>(
    () => brokerCredentialsService.getActiveCredentialId()
  );

  const verifyKey = async (id: string) => {
    setVerifying(id);
    try {
      const result = await brokerCredentialsService.verifyCredential(id);
      setVerdicts((prev) => ({
        ...prev,
        [id]: {
          ok: result.verified,
          error: result.error,
          venue: result.venue,
          available: result.available_balance,
          total: result.total_balance,
        },
      }));
      if (!result.verified) {
        // The exchange's own words. "Rejected" and "unreachable" need different
        // actions from the user, and only its message tells them apart.
        toast({
          title: t('broker.toast.rejectedTitle'),
          description: result.error || t('broker.toast.rejectedDesc'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('broker.toast.acceptedTitle'),
          description: t('broker.toast.acceptedDesc'),
        });
      }
    } catch (e: any) {
      setVerdicts((prev) => ({ ...prev, [id]: { ok: false, error: e?.message } }));
      toast({
        title: t('broker.toast.unreachableTitle'),
        description: t('broker.toast.unreachableDesc'),
        variant: 'destructive',
      });
    } finally {
      setVerifying(null);
    }
  };
  const [showPassKey, setShowPassKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  /*
   * The key form's funnel (2026-10-07): opened, submitted, refused and why,
   * connected after how many tries. The step most people give up on, measured
   * at last. Never the key: only the broker, the attempt and the error class.
   */
  const openedAt = useRef(0);
  const attempts = useRef(0);
  useEffect(() => {
    if (open) {
      openedAt.current = Date.now();
      attempts.current = 0;
      track('broker_keys_opened', undefined, undefined, { entry: window.location.pathname });
      loadCredentials();
    }
  }, [open]);

  const loadCredentials = async () => {
    const loadedCredentials = await brokerCredentialsService.getAllCredentials();
    setCredentials(loadedCredentials);
    setCredentialsLoaded(true);
    // Re-read the pin alongside the list: handleSetActive writes it and then
    // calls this, so without it the badge would not move until a remount.
    setActiveCredentialId(brokerCredentialsService.getActiveCredentialId());
  };

  const resetForm = () => {
    setConnectionName('');
    setPassKey('');
    setSecretKey('');
    setPassphrase('');
    setShowPassKey(false);
    setShowSecretKey(false);
    setShowPassphrase(false);
  };

  const handleSave = async () => {
    const baseLabel = connectionName.trim();
    if (!baseLabel) {
      toast({
        title: t('broker.toast.validationError'),
        description: t('broker.toast.nameRequired'),
        variant: "destructive",
      });
      return;
    }

    // Validate with Zod
    const validation = brokerKeySchema.safeParse({
      passKey,
      secretKey,
      passphrase,
      isTestnet
    });

    if (!validation.success) {
      const firstError = validation.error.issues[0];
      toast({
        title: t('broker.toast.validationError'),
        description: t(firstError.message, { ns: 'messages', nsSeparator: false }),
        variant: "destructive",
      });
      return;
    }

    if (KEY_FIELDS[selectedBroker].passphrase && !(validation.data.passphrase || '').trim()) {
      toast({
        title: t('broker.toast.validationError'),
        description: t('broker.toast.passphraseRequired', { broker: selectedBroker }),
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);
      attempts.current += 1;
      track('broker_key_submitted', undefined, undefined, { broker: selectedBroker.toLowerCase(), attempt_number: attempts.current });
      const result = await brokerCredentialsService.saveCredential({
        name: selectedBroker,
        label: baseLabel,
        passKey: validation.data.passKey,
        secretKey: validation.data.secretKey,
        passphrase: KEY_FIELDS[selectedBroker].passphrase ? validation.data.passphrase || '' : '',
        isTestnet: validation.data.isTestnet,
      }, { attempt_number: attempts.current, ms_since_opened: Date.now() - openedAt.current });

      if (!result.success) {
        track('broker_key_failed', undefined, undefined, {
          broker: selectedBroker.toLowerCase(),
          error_class: keyErrorClass(result.error),
          attempt_number: attempts.current,
        });
        toast({
          title: t('broker.toast.error'),
          description: result.error || t('broker.toast.saveFailed'),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t('broker.toast.success'),
        description: t('broker.toast.saved', { broker: selectedBroker }),
      });

      resetForm();
      await loadCredentials();
      
      // Dispatch custom event to notify Trading page
      window.dispatchEvent(new CustomEvent('brokerConnectionChanged'));
    } catch (error) {
      toast({
        title: t('broker.toast.error'),
        description: t('broker.toast.saveFailed'),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (id: string) => {
    await brokerCredentialsService.setActiveCredential(id);
    await loadCredentials();
    toast({
      title: t('broker.toast.activeChangedTitle'),
      description: t('broker.toast.activeChangedDesc'),
    });
    window.dispatchEvent(new CustomEvent('brokerConnectionChanged'));
  };

  /*
   * A key in use cannot be deleted (Guillaume 11-03): "Pop-up saying 'Impossible
   * to delete, Key is currently in use. Please Stop session #, #, #'". Known
   * from the sessions already read, so the pop-up comes at once; the server
   * refuses the same (409 key_in_use) if the list was stale.
   */
  const [keyInUse, setKeyInUse] = useState<number[] | null>(null);
  const navigate = useNavigate();
  const sessionsOnKey = (id: string): number[] =>
    ((mySessions ?? []) as unknown as { id?: number | string; credential_id?: number; status?: string }[])
      .filter((x) => String(x.credential_id) === String(id) && x.status !== 'stopped')
      .map((x) => Number(x.id));
  const handleDelete = (id: string) => {
    const busy = sessionsOnKey(id);
    if (busy.length > 0) { setKeyInUse(busy); return; }
    setCredentialToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (credentialToDelete) {
      const removed = await brokerCredentialsService.removeCredential(credentialToDelete);
      await loadCredentials();
      if (!removed.ok && removed.inUseBy?.length) {
        setKeyInUse(removed.inUseBy);
      } else if (removed.ok) {
        const gone = credentials.find((c) => String(c.id) === String(credentialToDelete));
        track('broker_key_removed', undefined, undefined, { broker: (gone?.exchange_name ?? '').toLowerCase() });
        toast({ title: t('broker.toast.deleted'), description: t('broker.toast.deletedDesc') });
      } else {
        // The server's own sentence when it has one (open positions on this key).
        toast({ title: t('broker.toast.deleteFailed'), description: removed.error ?? t('broker.toast.deleteFailedDesc'), variant: "destructive" });
      }

      // Dispatch custom event to notify Trading page
      window.dispatchEvent(new CustomEvent('brokerConnectionChanged'));
    }
    setDeleteDialogOpen(false);
    setCredentialToDelete(null);
  };

  const fields = KEY_FIELDS[selectedBroker];
  const requiresPassphrase = Boolean(fields.passphrase);
  const isPreviewBroker = brokers.preview(selectedBroker);

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              {t('broker.title')}
            </DialogTitle>
            <DialogDescription>
              {t('broker.desc')}
            </DialogDescription>
          </DialogHeader>

          {/* No account yet? The keys below cannot exist without one. */}
          <PhemexReferralInline />

          {/* Security Warning */}
          <Card className="p-4 bg-destructive/10 border-destructive/20">
            <div className="flex gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm space-y-1">
                <p className="font-medium text-destructive">{t('broker.security.title')}</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>{t('broker.security.noShare')}</li>
                  <li>{t('broker.security.tradingOnly')}</li>
                  {selectedBroker !== 'Hyperliquid' && selectedBroker !== 'MetaTrader' && (
                    <li>
                      {/* The addresses come from lib/tradingIps.core, never from
                          the translated sentence: they change on a server move,
                          and a locale that kept an old one would show a dead
                          address to the users least able to tell it is dead.
                          Hyperliquid has no IP list: it signs instead. */}
                      {t('broker.security.ipShort')}
                      <TradingIpsCopy />
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </Card>

          {/* Add New Broker Form */}
          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="font-semibold">{t('broker.form.title')}</h3>
            
            <div className="space-y-2">
              <Label htmlFor="connectionName">{t('broker.form.apiName')}</Label>
              <Input
                id="connectionName"
                value={connectionName}
                onChange={(e) => setConnectionName(e.target.value)}
                placeholder={t('broker.form.apiNamePlaceholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t(isTestnet ? 'broker.form.savedAsTestnet' : 'broker.form.savedAsLive', {
                  name: connectionName.trim() || t('broker.form.unnamed'),
                })}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="broker">{t('broker.form.broker')}</Label>
              <Select
                value={selectedBroker}
                onValueChange={(value) => {
                  setSelectedBroker(value as BrokerName);
                  track('broker_selected', undefined, undefined, { broker: value.toLowerCase() });
                  // Never carried over to a broker whose key has no passphrase.
                  setPassphrase('');
                }}
              >
                <SelectTrigger id="broker">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {brokerOptions.map((broker) => (
                    <SelectItem key={broker} value={broker}>
                      <span className="flex items-center gap-2">
                        {broker}
                        {brokers.preview(broker) && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {t('broker.form.adminOnly')}
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isPreviewBroker && (
                <p className="text-xs text-muted-foreground">
                  {t('broker.form.previewNote', { broker: selectedBroker })}
                </p>
              )}
              {fields.note && <p className="text-xs text-muted-foreground">{t(fields.note)}</p>}
            </div>

            {selectedBroker === 'MetaTrader' ? (
              <MetaTraderPairing label={connectionName} onCreated={() => void loadCredentials()} />
            ) : (
            <>
            <div className="space-y-2">
              <Label htmlFor="passKey">{t(fields.key)}</Label>
              <div className="relative">
                <Input
                  id="passKey"
                  type={showPassKey ? 'text' : 'password'}
                  value={passKey}
                  onChange={(e) => setPassKey(e.target.value)}
                  placeholder={t(fields.keyPlaceholder)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute end-0 top-0 h-full"
                  onClick={() => setShowPassKey(!showPassKey)}
                  aria-label={t(showPassKey ? 'common:ui.hideValue' : 'common:ui.showValue', { field: t(fields.key) })}
                >
                  {showPassKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="secretKey">{t(fields.secret)}</Label>
              <div className="relative">
                <Input
                  id="secretKey"
                  type={showSecretKey ? 'text' : 'password'}
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={t(fields.secretPlaceholder)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute end-0 top-0 h-full"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  aria-label={t(showSecretKey ? 'common:ui.hideValue' : 'common:ui.showValue', { field: t(fields.secret) })}
                >
                  {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{tm('broker.neverOnPhone')}</p>
            </div>

            {requiresPassphrase && (
              <div className="space-y-2">
                <Label htmlFor="passphrase">{t(fields.passphrase ?? 'broker.form.passphrase')}</Label>
                <div className="relative">
                  <Input
                    id="passphrase"
                    type={showPassphrase ? 'text' : 'password'}
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    placeholder={t(fields.passphrasePlaceholder ?? 'broker.form.passphrasePlaceholder')}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute end-0 top-0 h-full"
                    onClick={() => setShowPassphrase(!showPassphrase)}
                    aria-label={t(showPassphrase ? 'common:ui.hideValue' : 'common:ui.showValue', { field: t(fields.passphrase ?? 'broker.form.passphrase') })}
                  >
                    {showPassphrase ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            <Button onClick={handleSave} className="w-full" disabled={saving}>
              {saving ? t('broker.form.saving') : t('broker.form.save')}
            </Button>
            </>
            )}
          </div>

          {/* Connected Brokers List, or the phone's line when there is none */}
          {credentials.length === 0 && credentialsLoaded && (
            <p className="text-sm text-muted-foreground text-center">{tm('broker.empty')}</p>
          )}
          {credentials.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold">{t('broker.list.title', { count: credentials.length })}</h3>
              <div className="space-y-2">
                {credentials.map((credential) => (
                  <Card
                    key={credential.id}
                    className={`p-4 ${keysInUse.has(Number(credential.id)) ? 'border-primary' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{credential.label || credential.exchange_name}</h4>
                          <Badge variant="outline" className="text-xs">
                            {credential.is_testnet ? t('broker.badge.testnet') : t('broker.badge.live')}
                          </Badge>
                          {/* The verdict, when we have one, takes precedence:
                              it is the exchange's answer, where "Active" below
                              is only our own row flag. A key can be enabled here
                              and refused there, which is exactly the state that
                              went unnoticed for a month. */}
                          {verdicts[String(credential.id)] ? (
                            verdicts[String(credential.id)].ok ? (
                              <Badge className="text-xs bg-success/20 text-success border-success/30">
                                <CheckCircle2 className="h-3 w-3 me-1" />
                                {t('broker.badge.accepted')}
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="text-xs">
                                {t('broker.badge.rejected')}
                              </Badge>
                            )
                          ) : null}
                          {/* In use = a running session trades on it (round 8 #227: a key under a
                              running session read "Inactive", from a flag of this browser's). */}
                          {sessionsLoading ? null : keysInUse.has(Number(credential.id)) ? (
                            <Badge className="text-xs">
                              <CheckCircle2 className="h-3 w-3 me-1" />
                              {t('broker.badge.inUse', { count: keysInUse.get(Number(credential.id)) })}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              <Circle className="h-3 w-3 me-1" />
                              {t('broker.badge.notInUse')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('broker.list.stored', { exchange: credential.exchange_name ?? '' })}
                        </p>
                        {/* The balance the exchange reported when this key was
                            last verified. The endpoint has always returned it
                            (handlers.go:313-318) and the client dropped it, so
                            "verified" was a claim with nothing behind it. A
                            number the user recognises is the proof — and a
                            balance that reads 0.00 on a funded account is how
                            somebody notices they pointed at testnet. */}
                        {verdicts[String(credential.id)]?.ok &&
                          verdicts[String(credential.id)]?.total !== undefined && (
                          <p className="text-xs text-muted-foreground">
                            {t('broker.list.balance', {
                              venue: verdicts[String(credential.id)]?.venue ?? t('broker.list.account'),
                              available: Number(verdicts[String(credential.id)]?.available ?? 0).toLocaleString(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                              total: Number(verdicts[String(credential.id)]?.total ?? 0).toLocaleString(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                            })}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {t('broker.list.added', { date: new Date(credential.created_at).toLocaleDateString(dateLocale()) })}
                        </p>
                        {/* CopyFi's fee, approved from the person's own wallet. Shows only where a fee is on offer. */}
                        {credential.exchange_name === 'hyperliquid' && <BuilderFeeApproval credentialId={String(credential.id)} />}
                        {(credential.exchange_name ?? '').startsWith('metatrader') && <MetaTraderKeyStatus credentialId={String(credential.id)} />}
                        {/* A key that ends on a date says how long is left; red inside the last two weeks. */}
                        {credential.key_expires_at && credential.key_days_left !== undefined && (
                          <p className={`text-xs ${credential.key_days_left <= 14 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                            {t('broker.list.keyExpires', {
                              date: new Date(credential.key_expires_at).toLocaleDateString(dateLocale()),
                              days: credential.key_days_left,
                            })}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {/* No "Set active" (round 8 #228): each session names its own key. */}
                        {/* Ask the exchange, on demand. The passive check runs
                            hourly on the server; this is for the moment someone
                            is standing in front of the screen wondering why
                            nothing is trading. */}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={verifying === String(credential.id)}
                          onClick={() => verifyKey(String(credential.id))}
                        >
                          {verifying === String(credential.id) ? t('broker.list.checking') : t('broker.list.checkKey')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(credential.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {verdicts[String(credential.id)]?.ok === false && (
                      <p className="text-xs text-danger mt-2">
                        {verdicts[String(credential.id)].error ||
                          t('broker.list.rejectedHelp')}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* A key in use: the sessions to stop, each a link to its page (11-03). */}
      <AlertDialog open={keyInUse !== null} onOpenChange={(o) => { if (!o) setKeyInUse(null); }}>
        <AlertDialogContent data-testid="key-in-use">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('broker.inUse.title')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>{t('broker.inUse.body')}</p>
                <p className="flex flex-wrap gap-2">
                  {(keyInUse ?? []).map((id) => (
                    <a key={id} href={`/session/${id}`} className="font-medium text-primary underline underline-offset-2"
                      onClick={(e) => { e.preventDefault(); setKeyInUse(null); setOpen(false); navigate(`/session/${id}`); }}>
                      {t('broker.inUse.session', { id })}
                    </a>
                  ))}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setKeyInUse(null)}>{t('broker.inUse.ok')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('broker.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('broker.delete.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('action.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('broker.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
